// Shared promise to prevent multiple concurrent init() calls
let initPromise: Promise<void> | null = null;

/**
 * Derives the market code (e.g. "US", "IE") from a locale string (e.g. "en-US", "en-IE").
 * Falls back to the full locale if no region segment is present.
 */
const getMarketFromLocale = (locale: string): string => {
  const parts = locale.split("-");
  return parts.length > 1 ? parts[parts.length - 1] : locale;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getPcxtV3 = (): any => (typeof document !== "undefined" ? (document as any).pcxtV3 : undefined);

/**
 * Ensures `document.pcxtV3` is loaded and initialized for the given tenant/locale.
 *
 * In UBIK the runtime loads `@vp/pricing-context-module` separately so pcxtV3 already
 * exists by the time our components run. In standalone usage (local dev, non-UBIK host
 * pages) nothing else loads it, so this function dynamically imports it and calls
 * `init()` to fire the INITIALIZED_EVENT that the rest of the pricing stack depends on.
 *
 * Safe to call concurrently — a shared promise prevents duplicate init() calls.
 */
export const ensurePricingContext = async (
  tenant: string,
  locale: string,
  developmentMode?: boolean,
): Promise<void> => {
  let pcxt = getPcxtV3();

  if (!pcxt) {
    try {
      await import("@vp/pricing-context-module");
      pcxt = getPcxtV3();
    } catch (error) {
      console.error("[price-platform] Failed to load @vp/pricing-context-module:", error);
      return;
    }
  }

  if (!pcxt) return;

  const market = getMarketFromLocale(locale);

  if (!pcxt.isInitialized(tenant, market, developmentMode)) {
    try {
      if (!initPromise) {
        initPromise = pcxt.init(tenant, market, developmentMode);
      }
      await initPromise;
      initPromise = null; // reset so future callers rely on isInitialized() check
    } catch (error) {
      initPromise = null; // allow retry on next call
      console.error("[price-platform] Failed to initialize pricing context:", error);
    }
  }
};
